/**
 * 應用程式進入點
 *
 * 還原自 index.js（pretty 檔 L3169-L3210）。
 * 啟動時從 localStorage 把上次的購物車、姓名、電話、訂單編號、LINE client id、
 * 折扣券、外送地址與取餐方式讀回 store —— 這是使用者重新整理後資料還在的原因。
 */

// 尚未轉為 async/await 的元件仍使用 regenerator 編譯形式，需要其執行期。
// 待 Products / VProduct / VCart / VLocation / Checkout 全部轉換完成後，
// 這一行即可移除。
import 'regenerator-runtime/runtime'

import axios from 'axios'
import Vue from 'vue'
import VueCookies from 'vue-cookies'
import * as VueGoogleMaps from 'vue2-google-maps'
// Checkout 的取餐時間用 `| moment('hh:mm A')` 濾鏡。原始 bundle 由 chunk-vendors
// 的模組 2ead 提供（vue-moment，內含 moment）。少了它濾鏡會回退成 identity，
// 時間會顯示成未格式化的 Date 字串。
import VueMoment from 'vue-moment'

import App from './App.vue'
import router from './router'
import store from './store'
import i18n from './i18n'
import { getStorage } from './utils'
import * as types from './store/mutation-types'

/*
 * 所有對後端的請求都要有 timeout。
 *
 * 這個專案原本一次都沒設（axios 0.22.0 的預設是 0 = 無限等待），
 * 而 Checkout.vue 的送單流程刻意在成功時不關閉 loading_show，讓遮罩撐到
 * 頁面跳走。兩者相加的後果是：只要後端有任何一條路徑沒有回應，
 * 客人就會看到一個永遠轉圈、擋住所有點擊、而且捲動被鎖住的全螢幕遮罩，
 * 只能強制關掉頁面，且不知道訂單究竟成不成立。
 *
 * 後端在 2026-08-20 之前有三條這樣的路徑（LINE Pay 回非 '0000'、
 * 街口 entry 回非 '000'、以及 createOrderItems 失敗的空 else），
 * 那三處現在都補上了回應。但前端不該依賴「後端每一條路徑都記得回應」——
 * 這一行是客戶端自己的保險。
 *
 * 45 秒是刻意訂得比後端最壞情況長：下單一路上後端可能等
 * geocode 10 秒 + Distance Matrix 10 秒 + LINE Pay 15 秒 = 35 秒。
 * 訂太短會把合法但緩慢的請求誤判成失敗，那比轉圈更糟 ——
 * 客人會以為沒成立而重送，變成重複訂單。
 * 這個值的用途是把「永遠」變成「有限」，不是讓畫面變快。
 */
axios.defaults.timeout = 45000

// 原始 build 中這些樣式在 chunk-common.css，全域套用且無 scoped。
// 之後轉 template 時可逐步拆分歸屬到各元件。
import './assets/vendors.css'
import './assets/main.css'

Vue.use(VueCookies)
Vue.use(VueMoment)

// ⚠️ 這把 key 原本就明文寫在 bundle 裡（client-side key 本質上是公開的），
//    但必須在 Google Cloud Console 設定 HTTP referrer 限制，只允許
//    micro-x2-sugar-291.com，否則會被他人盜用計費。
Vue.use(VueGoogleMaps, {
  load: {
    key: 'AIzaSyAHPvpb9jolTsNUWsTLvuEggDQ7dWlvbyE',
    libraries: ['geometry', 'places']
  }
})

Vue.config.productionTip = false

// 語言：唯一使用真正 cookie 的項目
if (Vue.$cookies.isKey('5ew41gf56ew456-language')) {
  const lang = Vue.$cookies.get('5ew41gf56ew456-language')

  if (lang in i18n.messages) {
    store.commit(types.LANGUAGE, lang)
    i18n.locale = lang
  }
}

// 其餘狀態從帶有效期的 localStorage 還原
const restore = [
  ['5ew41gf56ew456-cart-list', types.CART_SET, JSON.parse],
  ['5ew41gf56ew456-user-name', types.NAME_SET],
  ['5ew41gf56ew456-user-number', types.NUMBER_SET],
  ['5ew41gf56ew456-remark', types.REMARK_SET],
  ['5ew41gf56ew456-order-id', types.ORDER_ID],
  ['5ew41gf56ew456-client-id', types.CLIENT_ID],
  ['5ew41gf56ew456-discount', types.DISCOUNT_SET],
  ['5ew41gf56ew456-location', types.LOCATION_SET],
  ['5ew41gf56ew456-order-type', types.ORDER_TYPE_SET]
]

restore.forEach(([key, mutation, transform]) => {
  const value = getStorage(key)
  if (value) store.commit(mutation, transform ? transform(value) : value)
})

new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App)
}).$mount('#app')
